<template>
	<div>
		<!-- One modal serves both PDF kinds (onboarding / checklist) -->
		<div class="modal fade os-modal" ref="modalEl" tabindex="-1" data-bs-backdrop="static" data-bs-keyboard="false" aria-hidden="true">
			<div class="modal-dialog modal-lg">
				<div class="modal-content" v-if="onboarding">
					<div class="modal-header">
						<div>
							<h5 class="os-modal-title">{{ kindLabel }} — {{ onboarding.name }}</h5>
							<p class="os-modal-sub">{{ kindDescription }}</p>
						</div>
						<button type="button" class="btn-close" aria-label="Close" @click="requestClose"></button>
					</div>
					<div class="modal-body">
						<div v-if="!pdfPath" class="os-note os-note--warn">
							<i class="fas fa-info-circle"></i>
							This onboarding doesn't have a generated {{ kindLabel }} yet — review the content below and click Generate.
						</div>

						<!-- Toolbar: sections left, download right -->
						<div class="os-pdf-toolbar">
							<div class="os-pdf-actions">
								<button v-if="kind === 'onboarding'" class="os-btn os-btn--sm" :class="{ 'os-btn--active': section === 'fields' }" @click="toggleSection('fields')">
									<i class="fas fa-list"></i> Filled Fields
								</button>
								<button class="os-btn os-btn--sm" :class="{ 'os-btn--active': section === 'editor' }" @click="toggleSection('editor')">
									<i class="fas fa-file-pdf"></i> Edit &amp; Generate
								</button>
								<button v-if="pdfPath" class="os-btn os-btn--sm" :class="{ 'os-btn--active': section === 'preview' }" @click="toggleSection('preview')">
									<i class="fas fa-eye"></i> Preview
								</button>
								<button class="os-btn os-btn--sm" :class="{ 'os-btn--active': section === 'history' }" @click="toggleSection('history')">
									<i class="fas fa-history"></i> History
								</button>
							</div>
							<a v-if="pdfPath" :href="storageUrl + pdfPath" target="_blank" class="os-btn os-btn--sm os-btn--success">
								<i class="fas fa-cloud-download-alt"></i> Download PDF
							</a>
						</div>
						<p class="os-hint mb-3">
							<template v-if="kind === 'onboarding'"><b>Filled Fields</b> shows the raw answers · </template>
							<b>Edit &amp; Generate</b> lets you tweak the content before generating<template v-if="pdfPath"> · <b>Preview</b> shows the current PDF</template> ·
							<b>History</b> lists previous versions.
						</p>

						<!-- Filled fields -->
						<div v-if="section === 'fields'" class="os-pdf-section">
							<div class="os-section-label">Filled fields on this onboarding</div>
							<div v-if="loadingFields" class="os-hint"><i class="fas fa-spinner fa-spin me-1"></i> Loading…</div>
							<div v-else-if="populatedFields.length === 0" class="os-hint">No fields have been filled in yet.</div>
							<div v-for="pField in populatedFields" :key="pField.id" class="os-ff-item">
								<div class="os-ff-name">{{ pField.name }}</div>
								<div class="os-ff-value">{{ pField.value }}</div>
							</div>
						</div>

						<!-- Editor -->
						<div v-if="section === 'editor'" class="os-pdf-section">
							<div v-if="loadingContent" class="os-hint mb-2"><i class="fas fa-spinner fa-spin me-1"></i> Preparing content…</div>
							<template v-else>
								<div v-if="!hadSavedContent" class="os-note">
									<i class="fas fa-magic"></i>
									The content below is auto-generated from the filled fields (with header and footer). Any edits you make are saved and reused next time.
								</div>
								<div v-else class="os-note">
									<i class="fas fa-info-circle"></i>
									This content was saved earlier — answers are <b>not</b> pulled in automatically.
									<template v-if="kind === 'onboarding'">
										<button
											class="os-btn os-btn--sm os-note-btn"
											:disabled="inserting"
											title="Adds newly filled answers and refreshes changed values — your manual edits stay untouched"
											@click="syncAnswers"
										>
											<i class="fas" :class="inserting ? 'fa-spinner fa-spin' : 'fa-sync'"></i> Sync answers
										</button>
									</template>
									<template v-else>
										<button class="os-btn os-btn--sm os-note-btn" @click="regenerateContent">Regenerate content</button>
									</template>
								</div>
								<div class="prepared-html-content">
									<editor api-key="" v-model="content" theme="advanced" theme_advanced_toolbar_location="bottom" :init="tinyPdfToolbar" />
								</div>
								<div class="d-flex justify-content-between align-items-center mt-3">
									<button class="os-btn" :disabled="saving" title="Save content for future use without generating the PDF" @click="saveContent">
										<i v-if="saving" class="fas fa-spinner fa-spin"></i>
										{{ saving ? 'Saving…' : 'Save Changes' }}
									</button>
									<button class="os-btn os-btn--primary" :disabled="generating" @click="generatePdf">
										<i class="fas" :class="generating ? 'fa-spinner fa-spin' : 'fa-file-pdf'"></i>
										{{ generating ? 'Generating…' : (pdfPath ? 'Regenerate PDF' : 'Generate PDF') }}
									</button>
								</div>
							</template>
						</div>

						<!-- Preview -->
						<div v-if="section === 'preview'" class="os-pdf-section os-pdf-section--preview">
							<iframe :src="previewUrl" class="os-pdf-preview" title="PDF preview"></iframe>
						</div>

						<!-- History -->
						<div v-if="section === 'history'" class="os-pdf-section">
							<div class="os-section-label">Previous versions</div>
							<div v-if="loadingVersions" class="os-hint"><i class="fas fa-spinner fa-spin me-1"></i> Loading…</div>
							<div v-else-if="versions.length === 0" class="os-hint">No generated versions yet — history starts with the next generated PDF.</div>
							<div v-for="version in versions" :key="version.id" class="os-ff-item os-version-item">
								<div>
									<div class="os-ff-name">{{ formatDateTime(version.created_at) }}</div>
									<div class="os-ff-value os-version-path">{{ version.path.split('/').pop() }}</div>
								</div>
								<a :href="storageUrl + version.path" target="_blank" class="os-btn os-btn--sm">
									<i class="fas fa-external-link-alt"></i> Open
								</a>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<span v-if="isDirty" class="os-dirty-hint"><i class="fas fa-circle"></i> Unsaved changes</span>
						<button type="button" class="os-btn" @click="requestClose">Close</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Unsaved changes guard -->
		<ConfirmModal
			ref="discardModal"
			title="Discard unsaved changes?"
			message="Your edits in the editor are not saved. Close anyway?"
			confirm-text="Discard"
			cancel-text="Keep editing"
			:danger="true"
			@confirm="forceClose"
		/>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import { Modal } from 'bootstrap'
import restApi from '@/api'
import Editor from '@tinymce/tinymce-vue'
import ConfirmModal from '@/components/common/ConfirmModal.vue'

const KIND_CONFIG = {
	onboarding: {
		label: 'Onboarding PDF',
		description: 'Summary document for the client, built from the answers collected during the onboarding call — only fields that were filled in are included.',
		pathField: 'onboarding_pdf',
		contentField: 'onboarding_pdf_content',
		generatedAtField: 'onboarding_pdf_generated_at',
		contentUpdatedAtField: 'onboarding_pdf_content_updated_at',
		prepareUrl: id => `/onboarding/${id}/prepare-pdf-content`,
		updateUrl: id => `/onboarding/${id}/update-onboarding-pdf`,
		generateUrl: id => `/onboarding/${id}/generate-onboarding-pdf`,
	},
	checklist: {
		label: 'Checklist PDF',
		description: 'To-do checklist for the client, built from the fields marked "Include in Checklist" on this onboarding type (see Settings).',
		pathField: 'checklist_pdf',
		contentField: 'checklist_pdf_content',
		generatedAtField: 'checklist_pdf_generated_at',
		contentUpdatedAtField: 'checklist_pdf_content_updated_at',
		prepareUrl: id => `/onboarding/${id}/prepare-checklist-pdf-content`,
		updateUrl: id => `/onboarding/${id}/update-checklist-pdf`,
		generateUrl: id => `/onboarding/${id}/generate-checklist-pdf`,
	},
}

export default {
	name: 'OnboardingPdfModals',
	components: { Editor, ConfirmModal },
	data() {
		return {
			modal: null,
			kind: 'onboarding',
			onboarding: null,
			section: 'editor',
			content: '',
			savedContent: '',
			hadSavedContent: false,
			populatedFields: [],
			versions: [],
			loadingContent: false,
			loadingFields: false,
			loadingVersions: false,
			saving: false,
			generating: false,
			inserting: false,
			previewBust: 0,
			tinyPdfToolbar: {
				menubar: false,
				plugins: 'link code lists',
				height: 420,
				content_style: 'p { padding: 0px; margin:0px }',
				toolbar1:
					'fontselect fontsizeselect formatselect lineheight | bold italic underline strikethrough | bullist numlist | alignleft aligncenter alignright alignjustify | link removeformat code',
			},
		}
	},
	computed: {
		...mapState(['storageUrl']),
		config() {
			return KIND_CONFIG[this.kind]
		},
		kindLabel() {
			return this.config.label
		},
		kindDescription() {
			return this.config.description
		},
		pdfPath() {
			return this.onboarding ? this.onboarding[this.config.pathField] : null
		},
		isDirty() {
			return !this.loadingContent && this.content !== this.savedContent
		},
		previewUrl() {
			if (!this.pdfPath) return ''
			return this.storageUrl + this.pdfPath + '?v=' + this.previewBust
		},
	},
	mounted() {
		this.modal = new Modal(this.$refs.modalEl)
	},
	beforeDestroy() {
		if (this.modal) this.modal.dispose()
	},
	methods: {
		/** Entry point — open the modal for an onboarding row. Mutates the
		 *  passed object on save/generate so the parent list stays in sync. */
		open(kind, onboarding) {
			this.kind = kind
			this.onboarding = onboarding
			this.section = 'editor'
			this.populatedFields = []
			this.versions = []
			this.previewBust = Date.now()
			const saved = onboarding[this.config.contentField]
			this.hadSavedContent = !!(saved && saved !== '')
			if (this.hadSavedContent) {
				this.content = saved
				this.savedContent = saved
			} else {
				this.content = ''
				this.savedContent = ''
				this.prepareContent()
			}
			this.modal.show()
		},
		toggleSection(name) {
			this.section = this.section === name ? '' : name
			if (name === 'fields' && this.section === 'fields') this.loadPopulatedFields()
			if (name === 'history' && this.section === 'history') this.loadVersions()
		},
		requestClose() {
			if (this.isDirty) {
				this.$refs.discardModal.open()
			} else {
				this.forceClose()
			}
		},
		forceClose() {
			this.$refs.discardModal.close()
			this.modal.hide()
			this.onboarding = null
			this.content = ''
			this.savedContent = ''
		},
		prepareContent() {
			this.loadingContent = true
			restApi
				.get(this.config.prepareUrl(this.onboarding.id))
				.then(({ data }) => {
					this.content = data
					this.savedContent = data
					this.loadingContent = false
				})
				.catch(error => {
					console.log(error)
					this.loadingContent = false
				})
		},
		regenerateContent() {
			this.hadSavedContent = false
			this.prepareContent()
		},
		loadPopulatedFields() {
			this.loadingFields = true
			restApi
				.get('/onboarding/' + this.onboarding.id + '/populated-fields')
				.then(({ data }) => {
					this.populatedFields = data
					this.loadingFields = false
				})
				.catch(error => {
					console.log(error)
					this.loadingFields = false
				})
		},
		loadVersions() {
			this.loadingVersions = true
			restApi
				.get('/onboarding/' + this.onboarding.id + '/pdf-versions', { params: { kind: this.kind } })
				.then(({ data }) => {
					this.versions = data
					this.loadingVersions = false
				})
				.catch(error => {
					console.log(error)
					this.loadingVersions = false
				})
		},
		/** Sync answers into the saved content without a full regenerate:
		 *  refreshes values of fields already in the content and inserts newly
		 *  filled fields INTO the answers block (right after the last existing
		 *  answer), not at the end of the document. Manual edits stay. */
		syncAnswers() {
			this.inserting = true
			restApi
				.get('/onboarding/' + this.onboarding.id + '/populated-fields')
				.then(({ data }) => {
					// TinyMCE may turn <b> into <strong> and sprinkle &nbsp; — normalize
					const norm = s =>
						String(s || '')
							.replace(/\u00a0/g, ' ')
							.replace(/\s+/g, ' ')
							.trim()
							.replace(/:$/, '')
					const container = document.createElement('div')
					container.innerHTML = this.content
					const paragraphs = [...container.querySelectorAll('p')]
					let updated = 0
					const missing = []
					let lastAnswerNode = null

					data.forEach(field => {
						const fieldName = norm(field.name)
						const newValue = String(field.value === null || field.value === undefined ? '' : field.value).trim()
						// A "name paragraph" contains only the bold field name
						const nameParagraph = paragraphs.find(p => {
							const bold = p.querySelector('b, strong')
							return bold && norm(p.textContent) === fieldName && norm(bold.textContent) === fieldName
						})
						if (!nameParagraph) {
							missing.push(field)
							return
						}
						// The value lives in the next <p> (skip <br> etc.)
						let valueEl = nameParagraph.nextElementSibling
						while (valueEl && valueEl.tagName !== 'P') valueEl = valueEl.nextElementSibling
						if (valueEl && !valueEl.querySelector('b, strong')) {
							if (norm(valueEl.textContent) !== norm(newValue)) {
								valueEl.textContent = newValue
								updated++
							}
						} else {
							// Name is there but the value paragraph is missing — add it
							valueEl = document.createElement('p')
							valueEl.textContent = newValue
							nameParagraph.after(valueEl)
							updated++
						}
						// Remember the deepest point of the answers block (skip trailing <br>)
						let tail = valueEl
						while (tail.nextSibling && tail.nextSibling.nodeName === 'BR') tail = tail.nextSibling
						if (!lastAnswerNode || tail.compareDocumentPosition(lastAnswerNode) & Node.DOCUMENT_POSITION_PRECEDING) {
							lastAnswerNode = tail
						}
					})

					// Insert new answers right after the last existing answer so they
					// land inside the answers block, before the footer
					if (missing.length > 0) {
						let anchor = lastAnswerNode
						missing.forEach(field => {
							const temp = document.createElement('div')
							temp.innerHTML = '<p><b>' + field.name + '</b></p><p>' + (field.value === null || field.value === undefined ? '' : field.value) + '</p><br>'
							const nodes = [...temp.childNodes]
							nodes.forEach(node => {
								if (anchor) {
									anchor.after(node)
								} else {
									// No existing answers at all — put them at the top
									container.prepend(node)
								}
								anchor = node
							})
						})
					}

					if (updated > 0 || missing.length > 0) {
						this.content = container.innerHTML
						const parts = []
						if (missing.length > 0) parts.push('added ' + missing.length + ' new')
						if (updated > 0) parts.push('updated ' + updated)
						this.$swal.fire({ icon: 'success', title: 'Answers synced — ' + parts.join(', '), timer: 2500 })
					} else {
						this.$swal.fire({ icon: 'info', title: 'Everything is already up to date', timer: 2000 })
					}
					this.inserting = false
				})
				.catch(error => {
					console.log(error)
					this.inserting = false
				})
		},
		saveContent() {
			this.saving = true
			restApi
				.post(this.config.updateUrl(this.onboarding.id), { content: this.content })
				.then(() => {
					this.savedContent = this.content
					this.hadSavedContent = true
					// Sync the list row so a reopened modal shows the same state
					this.$set(this.onboarding, this.config.contentField, this.content)
					this.$set(this.onboarding, this.config.contentUpdatedAtField, new Date().toISOString())
					this.saving = false
					this.$swal.fire({ icon: 'success', title: 'Content saved!', timer: 2000 })
				})
				.catch(error => {
					console.log(error)
					this.saving = false
					this.$swal.fire({ icon: 'error', title: 'Saving the content failed', text: String(error) })
				})
		},
		generatePdf() {
			this.generating = true
			restApi
				.post(this.config.generateUrl(this.onboarding.id), { content: this.content })
				.then(({ data }) => {
					const now = new Date().toISOString()
					this.savedContent = this.content
					this.hadSavedContent = true
					this.$set(this.onboarding, this.config.pathField, data)
					this.$set(this.onboarding, this.config.contentField, this.content)
					this.$set(this.onboarding, this.config.generatedAtField, now)
					this.$set(this.onboarding, this.config.contentUpdatedAtField, now)
					this.previewBust = Date.now()
					this.generating = false
					this.section = 'preview'
					this.$swal.fire({ icon: 'success', title: this.kindLabel + ' generated!', timer: 2000 })
				})
				.catch(error => {
					console.log(error)
					this.generating = false
					this.$swal.fire({ icon: 'error', title: 'Generating the PDF failed', text: String(error) })
				})
		},
		formatDateTime(dateStr) {
			if (!dateStr) return ''
			const date = new Date(dateStr)
			if (Number.isNaN(date.getTime())) return ''
			return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
		},
	},
}
</script>

<style scoped>
/* Flat Juniper modal look */
.os-modal ::v-deep .modal-content {
	border: 1px solid #e6e8ec;
	border-radius: 14px;
}

.os-modal .modal-header {
	border-bottom: none;
	padding-bottom: 4px;
	align-items: flex-start;
}

.os-modal .modal-footer {
	border-top: none;
	padding-top: 4px;
	gap: 10px;
	align-items: center;
}

.os-modal-title {
	font-size: 15px;
	font-weight: 700;
	color: #111827;
	margin: 0;
}

.os-modal-sub {
	font-size: 12px;
	color: #8a909c;
	margin: 3px 0 0;
	line-height: 1.4;
}

.os-hint {
	font-size: 12px;
	color: #8a909c;
	line-height: 1.45;
	margin: 5px 0 0;
}

.os-note {
	display: flex;
	align-items: baseline;
	gap: 8px;
	background: #f8f9fb;
	border: 1px solid #eef0f4;
	border-radius: 8px;
	padding: 9px 12px;
	font-size: 12.5px;
	color: #4a5260;
	line-height: 1.5;
	margin-bottom: 12px;
}

.os-note i {
	color: #9ca3af;
	font-size: 12px;
	flex-shrink: 0;
	position: relative;
	top: 1px;
}

.os-note--warn {
	background: #fffbeb;
	border-color: #fde68a;
	color: #92400e;
}

.os-note--warn i {
	color: #d97706;
}

.os-note-btn {
	margin-left: 6px;
}

.os-pdf-toolbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-top: 2px;
	flex-wrap: wrap;
}

.os-pdf-actions {
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
}

.os-pdf-section {
	border: 1px solid #eef0f4;
	border-radius: 10px;
	padding: 14px;
	margin-top: 12px;
}

.os-pdf-section--preview {
	padding: 6px;
}

.os-pdf-preview {
	width: 100%;
	height: 560px;
	border: none;
	border-radius: 8px;
	background: #f6f7f9;
}

.os-section-label {
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #6b7280;
	margin-bottom: 10px;
}

.os-ff-item {
	padding: 7px 0;
	border-bottom: 1px solid #f1f3f5;
}

.os-ff-item:last-child {
	border-bottom: none;
}

.os-ff-name {
	font-size: 12px;
	font-weight: 600;
	color: #374151;
}

.os-ff-value {
	font-size: 13px;
	color: #6b7280;
	white-space: pre-wrap;
	word-break: break-word;
}

.os-version-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}

.os-version-path {
	font-size: 11px;
}

.os-dirty-hint {
	margin-right: auto;
	font-size: 12px;
	color: #d97706;
	display: inline-flex;
	align-items: center;
	gap: 6px;
}

.os-dirty-hint i {
	font-size: 7px;
}

/* Flat buttons — cd-btn design */
.os-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.4rem;
	padding: 0.45rem 0.9rem;
	border: 1px solid #e6e8ec;
	border-radius: 9px;
	font-size: 0.85rem;
	font-weight: 600;
	background: transparent;
	color: #2b303b;
	cursor: pointer;
	text-decoration: none;
	transition: all 0.12s ease;
}

.os-btn:hover:not(:disabled) {
	background: #f3f5f9;
	color: #2b303b;
	text-decoration: none;
}

.os-btn:disabled {
	opacity: 0.55;
	cursor: not-allowed;
}

.os-btn--sm {
	padding: 0.25rem 0.6rem;
	font-size: 0.78rem;
	border-radius: 7px;
	gap: 0.3rem;
}

.os-btn--active {
	background: #f3f5f9;
	border-color: #d1d5db;
	color: #111827;
}

.os-btn--primary {
	color: #2b6cff;
	border-color: #b6cfff;
}

.os-btn--primary:hover:not(:disabled) {
	background: #ebf2ff;
	border-color: #8fb4ff;
	color: #2b6cff;
}

.os-btn--success {
	color: #16a34a;
	border-color: #bbf7d0;
}

.os-btn--success:hover:not(:disabled) {
	background: #dcfce7;
	border-color: #86efac;
	color: #15803d;
}

.os-btn i {
	font-size: 0.72em;
}

/* design.scss forces a 360px min-height on all TinyMCE instances */
.os-modal ::v-deep .tox.tox-tinymce {
	min-height: 320px;
}

.prepared-html-content ::v-deep p {
	padding: 0px;
	margin: 0px;
}
</style>
